import { FC } from 'react'

import { RenderIf } from 'components/RenderIf'
import { useMySelector } from 'featureSliced/shared/hooks/useMySelector'
import { useTranslations } from 'featureSliced/shared/hooks/useTranslations'
import { useGetTranslatedNames } from 'featureSliced/shared/hooks/useGetTranslatedNames'
import { ICategory } from 'featureSliced/shared/store/reducers/CategoriesReducer/types'
import { Time } from 'featureSliced/widgets/TimeLocationNew/Time'
import { Location } from 'featureSliced/widgets/TimeLocationNew/Location'

import {
  WebCard,
  WebCardLabel,
  WebCardValue,
  WebCategoryButton,
  WebCategoryList,
  WebDivider,
  WebGroupTitle,
  WebLink,
  WebSide,
  WebTableCard,
} from './styled'

type WebSideColumnProps = {
  categories: ICategory[]
  activeCategoryId: number | null
  onCategoryClick: (id: number) => void
  /** C&C only — opens the "are you sure" prompt that re-runs the pickup wizard */
  editTimeLocation?: () => void
  /** QR only — opens the table picker */
  onTableClick?: () => void
}

const IS_CLICK_COLLECT = process.env.REACT_APP_IS_CLIC === '1'

export const WebSideColumn: FC<WebSideColumnProps> = ({
  categories,
  activeCategoryId,
  onCategoryClick,
  editTimeLocation,
  onTableClick,
}) => {
  const t = useTranslations()
  const { getCategoryName } = useGetTranslatedNames()
  const { tableData } = useMySelector((state) => state.common)

  const tableCaption = tableData?.caption || tableData?.objectTag

  return (
    <WebSide data-testid="web-side-column">
      {/* C&C: pickup context. QR-Menu: the table number — never in the header. */}
      <RenderIf condition={IS_CLICK_COLLECT}>
        <WebCard>
          <Time />
          <WebDivider />
          <Location />
          <RenderIf condition={!!editTimeLocation}>
            <WebLink type="button" onClick={editTimeLocation}>
              {t('buttons.change')}
            </WebLink>
          </RenderIf>
        </WebCard>
      </RenderIf>

      <RenderIf condition={!IS_CLICK_COLLECT && !!tableCaption}>
        <WebTableCard type="button" onClick={onTableClick}>
          <WebCardLabel>{t('common.tableNumber')}</WebCardLabel>
          <WebCardValue>{tableCaption}</WebCardValue>
        </WebTableCard>
      </RenderIf>

      <div>
        <WebGroupTitle>{t('common.categories')}</WebGroupTitle>
        <WebCategoryList>
          {categories?.map((category) => (
            <WebCategoryButton
              key={category.id}
              type="button"
              isActive={activeCategoryId === category.id}
              onClick={() => onCategoryClick(category.id)}
            >
              <span>{getCategoryName(category)}</span>
              <span>{category.products?.length || 0}</span>
            </WebCategoryButton>
          ))}
        </WebCategoryList>
      </div>
    </WebSide>
  )
}
